import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV } from "@/lib/content";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      // active section
      const y = window.scrollY + 120;
      let current = "home";
      for (const n of NAV) {
        const el = document.getElementById(n.id);
        if (el && el.offsetTop <= y) current = n.id;
      }
      setActive(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 inset-x-0 z-40 transition-colors duration-500 ${
          scrolled ? "bg-ink-950/75 backdrop-blur-md border-b border-ivory/[0.06]" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 h-16 md:h-20 flex items-center justify-between">
          <a href="#home" data-testid="brand-mark" className="flex items-center gap-3 group">
            <span className="font-mono-caps text-[10px] text-copper">PM</span>
            <span className="h-3 w-px bg-ivory/20" />
            <span className="font-serif text-lg md:text-xl text-ivory tracking-tight italic">
              Parin Majithia
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                data-testid={`nav-link-${n.id}`}
                className={`text-[11px] font-mono-caps link-underline transition-colors duration-300 ${
                  active === n.id ? "text-copper" : "text-ivory/70 hover:text-ivory"
                }`}
              >
                {n.label}
              </a>
            ))}
          </nav>

          <a
            href="#contact"
            data-testid="nav-cta-contact"
            className="hidden md:inline-flex items-center gap-2 text-[11px] font-mono-caps text-ivory border border-ivory/20 hover:border-copper hover:text-copper px-4 py-2.5 transition-colors duration-300"
          >
            Enquire
            <span className="text-copper">→</span>
          </a>

          <button
            data-testid="mobile-menu-toggle"
            aria-label="Open menu"
            onClick={() => setOpen(true)}
            className="lg:hidden text-ivory p-2 -mr-2"
          >
            <Menu size={22} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-50 bg-ink-950/98 backdrop-blur-xl"
            data-testid="mobile-menu-overlay"
          >
            <div className="max-w-[1600px] mx-auto px-6 sm:px-10 h-16 md:h-20 flex items-center justify-between">
              <span className="font-serif italic text-xl text-ivory">Parin Majithia</span>
              <button
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                className="text-ivory p-2 -mr-2"
              >
                <X size={22} />
              </button>
            </div>
            <div className="px-6 sm:px-10 pt-12 pb-16 flex flex-col gap-2">
              {NAV.map((n, i) => (
                <motion.a
                  key={n.id}
                  href={`#${n.id}`}
                  data-testid={`mobile-nav-link-${n.id}`}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 + i * 0.05, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="group flex items-baseline justify-between border-b border-ivory/[0.06] py-4"
                >
                  <span className="font-serif text-4xl md:text-5xl text-ivory group-hover:text-copper transition-colors duration-300">
                    {n.label}
                  </span>
                  <span className="font-mono-caps text-[10px] text-ivory/40">
                    0{i + 1}
                  </span>
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
